import { HttpHeaders } from "@angular/common/http";
import { DgDayOfWeek } from "@core/models/domain/restriction.domain";

/**
 * Extracts the value of a specific header from an `HttpHeaders` object.
 *
 * This function retrieves the value of the specified header key from the `HttpHeaders` object.
 * Returns an empty string if the header is not found.
 *
 * @param {HttpHeaders} headers - The `HttpHeaders` object containing the headers.
 * @param {string} key - The key of the header to be extracted.
 * @returns {string} The value of the specified header or an empty string if not found.
 */
export function extractHeaderValue(headers: HttpHeaders, key: string): string {
    return headers.get(key) || "";
  }

/**
 * Handles errors encountered during an operation.
 *
 * This function logs the error and throws it to propagate the failure.
 *
 * @template T
 * @param {string} operation - The name of the operation during which the error occurred.
 * @param {any} error - The error object encountered during the operation.
 * @returns {never} Throws the error to propagate the failure.
 */
export function handleError<T>(operation: string, error: any): never {
    console.error(`${operation} error:`, error);
    throw error;
}

/**
 * Constructs a complete API endpoint URL.
 *
 * This function combines the base API URL and the endpoint path to form the complete URL.
 *
 * @param {string} apiUrl - The base API URL.
 * @param {string} endpoint - The endpoint path to be appended to the base URL.
 * @returns {string} The complete API endpoint URL.
 */
export function buildApiEndpoint(apiUrl: string, endpoint: string): string {
    return `${apiUrl}/${endpoint}`;
}

/**
 * Converts an array of day strings to an array of `DgDayOfWeek` enums.
 *
 * This function maps each day string to its corresponding enum value.
 *
 * @param {Array<string>} repeatSelectedDays - An array of day strings to be converted.
 * @returns {DgDayOfWeek[]} An array of `DgDayOfWeek` enums corresponding to the input day strings.
 */
export function getRepeatSelectedDays(repeatSelectedDays: Array<string>): DgDayOfWeek[] {
  let days: Array<DgDayOfWeek> = [];
  repeatSelectedDays.forEach((day) => {
    switch (day) {
      case "MONDAY":
        days.push(DgDayOfWeek.MONDAY);
        break;

      case "TUESDAY":
        days.push(DgDayOfWeek.TUESDAY);
        break;

      case "WEDNESDAY":
        days.push(DgDayOfWeek.WEDNESDAY);
        break;

      case "THURSDAY":
        days.push(DgDayOfWeek.THURSDAY);
        break;

      case "FRIDAY":
        days.push(DgDayOfWeek.FRIDAY);
        break;

      case "SATURDAY":
        days.push(DgDayOfWeek.SATURDAY);
        break;

      case "SUNDAY":
        days.push(DgDayOfWeek.SUNDAY);
        break;
    }
  });
  return days;
}